import { Box, Heading, HeadingProps, Stack, Text, TextProps } from '@chakra-ui/react';
import { createElement, isValidElement } from 'react';

import { Button } from '../Button';

type PlaceholderProps = {
  /**
   * Компонент иконки — или готовый элемент, когда это не иконка (спиннер на экране обновления).
   * Компонент рисуется одним размером и цветом на всех пустых страницах: раньше каждая
   * страница задавала его сама — 60px, 50px белым, вовсе без размера, — и одно и то же
   * состояние «здесь пусто» выглядело на трёх экранах тремя разными способами.
   */
  icon: React.ComponentType | React.ReactNode;
  children: React.ReactNode;
};

/**
 * Пустая **страница**: лента без заявок, избранное, поиск без результатов, 404.
 *
 * Не путать с `ListEmpty` — пустым **списком внутри экрана**, рядом с которым есть другое
 * содержимое. Это две роли, а не два вкуса одного компонента: страница крупная и стоит по центру
 * свободного места, список компактный. Разница между ними — в размере и месте, а не в наборе
 * частей: иконка, заголовок, описание, действие — одни и те же.
 *
 * Переехал сюда из папки дашборда: его зовут блог, чат и страницы ошибок, и общее должно лежать
 * в общем месте, иначе его не находят и заводят рядом второй такой же.
 */
export const Placeholder = ({ children, icon }: PlaceholderProps) => (
  <Stack
    pt={{ base: 8, md: 40 }}
    pb={8}
    gap={5}
    justifyContent="center"
    alignSelf="center"
    alignItems="center"
  >
    {/* fg.icon, а не ступень fg: ступени полупрозрачны, и на пересечениях штрихов иконки альфа
        складывается в пятна. Почему так — в самом токене. */}
    {/* Готовый элемент отличаем через `isValidElement`, а не через `typeof === 'function'`:
        иконки из плагина и обёрнутые в `forwardRef` — это объекты, и проверка по типу
        отправляла их рендериться как ребёнка. На сервере это падение всей страницы. */}
    {isValidElement(icon) ? (
      icon
    ) : (
      /* Коробка, а не `Icon as={...}`: `as` подменяет сам элемент, и размер до иконки не
         доезжал — каждая рисовалась своим (50px), мимо этой строки. Разбор в `ListEmpty`. */
      <Box color="fg.icon" boxSize="12" css={{ '& > svg': { width: '100%', height: '100%' } }}>
        {createElement(icon as React.ComponentType)}
      </Box>
    )}
    {children}
  </Stack>
);

Placeholder.Button = Button;

// Роль `subtitle` целиком, без перебивки веса: раньше здесь стоял `heading` с весом 500,
// то есть роль, которой в наборе нет. Заголовок пустой страницы — не заголовок страницы
// по иерархии, поэтому ступень ниже, но своим весом, а не подкрученным.
Placeholder.Title = ({ children, ...props }: HeadingProps) => (
  <Heading textStyle="subtitle" textAlign="center" {...props}>
    {children}
  </Heading>
);

Placeholder.Description = ({ children, ...props }: TextProps) => (
  <Text textStyle="body" color="fg.muted" textAlign="center" {...props}>
    {children}
  </Text>
);
